import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * VisualizationModule - Handles daily visualization exercise with text-to-speech
 * 
 * @param {Object} props
 * @param {boolean} props.completedToday - Whether visualization was completed today
 * @param {Function} props.onComplete - Callback when user completes visualization
 */
const VisualizationModule = ({ completedToday, onComplete }) => {
  const [visualization, setVisualization] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);

  // Check if speech synthesis is supported
  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      setSpeechSupported(true);
    }
  }, []);

  // Load visualization on mount
  useEffect(() => {
    loadVisualization();
  }, []);

  // Cleanup speech synthesis on unmount
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const loadVisualization = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/visualizations/daily`, {
        timeout: 30000 // 30 second timeout for Claude API
      });
      setVisualization(response.data);
    } catch (error) {
      console.error('Visualization error:', error);
      if (error.response) {
        toast.error(`Failed to load visualization: ${error.response.status} ${error.response.statusText}`);
      } else if (error.request) {
        toast.error('Failed to load visualization: No response from server');
      } else {
        toast.error('Failed to load visualization: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const readVisualization = () => {
    if (!speechSupported || !visualization) {
      toast.error('Text-to-speech is not supported in your browser');
      return;
    }

    // Stop any ongoing speech
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }

    if (isReading) {
      setIsReading(false);
      return;
    }

    // Add natural pauses between sections for better flow
    const textToRead = `
      ${visualization.title}... 
      
      ${visualization.scenario}... 
      
      Focus on: ${visualization.focus}...
      
      ${visualization.personalization ? visualization.personalization : ''}
    `;

    const utterance = new SpeechSynthesisUtterance(textToRead);
    
    // Get all available voices
    let voices = typeof window !== 'undefined' && 'speechSynthesis' in window 
      ? window.speechSynthesis.getVoices() 
      : [];
    
    // If voices aren't loaded yet, wait for them
    if (voices.length === 0 && typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.addEventListener('voiceschanged', () => {
        voices = window.speechSynthesis.getVoices();
      });
    }
    
    // Select the most natural-sounding voice with priority order
    const preferredVoice = voices.find(voice => {
      const name = voice.name.toLowerCase();
      const lang = voice.lang.toLowerCase();
      
      // Prioritize premium/natural voices (these are typically higher quality)
      return (
        // Google voices (usually very natural)
        name.includes('google') ||
        name.includes('wavenet') ||
        // Microsoft natural voices
        name.includes('neural') ||
        name.includes('natural') ||
        // Apple enhanced voices
        name.includes('enhanced') ||
        name.includes('premium') ||
        // Samantha is a high-quality voice on Mac
        name.includes('samantha') ||
        // Look for US English voices as fallback
        (lang.startsWith('en-us') && !name.includes('basic'))
      );
    }) || voices.find(voice => voice.lang.startsWith('en')) || voices[0];
    
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    
    // Optimized settings for more natural, meditative speech
    utterance.rate = 0.85;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    utterance.lang = 'en-US';

    utterance.onstart = () => {
      setIsReading(true);
      toast.success('🎧 Visualization reading started. Close your eyes and listen...');
    };

    utterance.onend = async () => {
      setIsReading(false);
      toast.info('✅ Visualization reading complete');
      
      // Automatically mark visualization as complete and award points
      if (!completedToday) {
        await onComplete("Completed visualization through audio guidance");
      }
    };

    utterance.onerror = (event) => {
      setIsReading(false);
      toast.error('Failed to read visualization');
      console.error('Speech synthesis error:', event);
    };

    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleManualComplete = async () => {
    await onComplete("I Have Completed This Visualization 🧘‍♂️");
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-pulse text-yellow-400 mb-2 text-lg">
          ✨ Generating your personalized visualization...
        </div>
        <p className="text-gray-300 text-sm">This may take up to 30 seconds</p>
      </div>
    );
  }

  if (!visualization) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-500">
          <h3 className="text-blue-800 font-bold text-xl mb-2">{visualization.title}</h3>
          <div className="text-blue-700 space-y-3">
            <p className="leading-relaxed">{visualization.scenario}</p>
            {visualization.personalization && (
              <p className="text-sm bg-blue-100 p-2 rounded italic">
                <strong>Personalized for you:</strong> {visualization.personalization}
              </p>
            )}
            <p className="text-sm font-medium">
              <strong>Focus Areas:</strong> {visualization.focus}
            </p>
          </div>
        </div>
        
        <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
          <p>
            <strong>💡 Visualization Tip:</strong> Close your eyes, take deep breaths, 
            and engage all your senses. Feel the ball, hear the crowd, see the field, 
            and experience the emotions of success.
          </p>
        </div>
      </div>
      
      {/* Read Aloud Control */}
      {speechSupported && !completedToday && (
        <div className="flex gap-2">
          <Button
            onClick={readVisualization}
            variant="outline"
            className="flex-1 border-blue-500 text-blue-600 hover:bg-blue-50 flex items-center justify-center gap-2"
            disabled={loading}
          >
            {isReading ? (
              <>
                <span className="animate-pulse">⏸️</span>
                <span>Stop Reading</span>
              </>
            ) : (
              <>
                <span>🎧</span>
                <span>Read Aloud (Auto-Complete)</span>
              </>
            )}
          </Button>
        </div>
      )}
      
      {!completedToday && !isReading && (
        <div className="text-center">
          <p className="text-sm text-gray-500 mb-2">or</p>
          <Button 
            onClick={handleManualComplete}
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
          >
            {loading ? 'Marking Complete...' : 'I Have Completed This Visualization 🧘‍♂️'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default VisualizationModule;
